<script setup lang="ts">
/**
 * Indicateurs de pilotage d'une demande (réservés WRITE/ADMIN) :
 *   1. Délai d'attribution      (création → attribution, ouvré)
 *   2. Délai de prise en charge (attribution → prise en charge, ouvré)
 *   3. Temps passé              (cumul de l'effort saisi sur les actions)
 *   4. Temps de traitement      (ouverture → fermeture, ouvré)
 */
import { computed } from 'vue'
import {
  ClockIcon,
  BoltIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
} from '@heroicons/vue/24/outline'
import type { AccompRequest } from '@/v2/accompagnement/api/accompagnementTypes'
import {
  acceptDelayMs,
  assignDelayMs,
  resolveDelayMs,
  totalTimeSpentMinutes,
  formatDuration,
  formatMinutes,
} from '@/v2/accompagnement/helpers/accompagnementDelays'

const props = defineProps<{ request: AccompRequest }>()

const assign = computed(() => assignDelayMs(props.request))
const accept = computed(() => acceptDelayMs(props.request))
const resolve = computed(() => resolveDelayMs(props.request))
const spentMinutes = computed(() => totalTimeSpentMinutes(props.request))

const assignHint = computed(() =>
  props.request.assignedAt ? 'Création → attribution' : "En attente d'attribution",
)
const acceptHint = computed(() => {
  if (props.request.acceptedAt) return 'Attribution → prise en charge'
  if (props.request.assignedAt) return 'Pas encore prise en charge'
  return 'En attente'
})
const spentHint = computed(() =>
  spentMinutes.value > 0 ? 'Cumul saisi sur les actions' : 'Aucun temps saisi',
)
const resolveHint = computed(() =>
  props.request.closedAt ? 'Ouverture → fermeture' : 'Demande en cours',
)
</script>

<template>
  <div class="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
    <div class="card border-l-4 border-secondary bg-base-100 shadow-sm">
      <div class="card-body gap-0.5 p-3">
        <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
          <ClockIcon class="h-4 w-4" aria-hidden="true" />
          Délai d'attribution
        </p>
        <p class="text-xl font-bold leading-tight tabular-nums">{{ formatDuration(assign) }}</p>
        <p class="text-sm text-base-content/70">{{ assignHint }}</p>
      </div>
    </div>

    <div class="card border-l-4 border-primary bg-base-100 shadow-sm">
      <div class="card-body gap-0.5 p-3">
        <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
          <BoltIcon class="h-4 w-4" aria-hidden="true" />
          Délai de prise en charge
        </p>
        <p class="text-xl font-bold leading-tight tabular-nums">{{ formatDuration(accept) }}</p>
        <p class="text-sm text-base-content/70">{{ acceptHint }}</p>
      </div>
    </div>

    <div class="card border-l-4 border-accent bg-base-100 shadow-sm">
      <div class="card-body gap-0.5 p-3">
        <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
          <ClipboardDocumentCheckIcon class="h-4 w-4" aria-hidden="true" />
          Temps passé
        </p>
        <p class="text-xl font-bold leading-tight tabular-nums">
          {{ spentMinutes > 0 ? formatMinutes(spentMinutes) : '—' }}
        </p>
        <p class="text-sm text-base-content/70">{{ spentHint }}</p>
      </div>
    </div>

    <div class="card border-l-4 border-success bg-base-100 shadow-sm">
      <div class="card-body gap-0.5 p-3">
        <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
          <CheckCircleIcon class="h-4 w-4" aria-hidden="true" />
          Temps de traitement
        </p>
        <p class="text-xl font-bold leading-tight tabular-nums">{{ formatDuration(resolve) }}</p>
        <p class="text-sm text-base-content/70">{{ resolveHint }}</p>
      </div>
    </div>
  </div>
</template>
